import { api } from './api';

export interface RedzoneDowntimeSkuOption {
  sku: string;
  product_type_name: string;
  historical_runs: number;
  average_oee_pct: number | null;
  average_target_oee_pct: number | null;
  average_oee_delta_pp: number | null;
  below_target_segments: number;
  availability_problem_events: number;
  availability_affected_runs: number;
  availability_frequency_pct: number | null;
  availability_hours_lost: number;
}

export type RedzoneDynamicDimension =
  | 'sku'
  | 'area'
  | 'location'
  | 'problem_type'
  | 'asset_type'
  | 'asset_name';
export type RedzoneDynamicFacetDimension = RedzoneDynamicDimension | 'plant';

export interface RedzoneDynamicFacet {
  dimension: RedzoneDynamicFacetDimension;
  value: string;
  label: string;
  parent_value: string | null;
  record_count: number;
}

export type RedzoneDynamicFilters = Partial<Record<RedzoneDynamicDimension, string[]>>;
export interface RedzoneDynamicPeriod {
  start_month: string | null;
  end_month: string | null;
  label: string;
}

export type RedzonePlannedMode = 'unplanned' | 'planned' | 'both';

export interface RedzoneDowntimeArea {
  metric_level: string;
  sku: string;
  product_type_name: string;
  area_name: string;
  historical_runs: number;
  historical_run_uuid_segments: number;
  average_run_time_minutes: number | null;
  average_oee_pct: number | null;
  average_target_oee_pct: number | null;
  average_oee_delta_pp: number | null;
}

export interface RedzoneDowntimeLocation extends RedzoneDowntimeArea {
  location_name: string;
  affected_runs: number;
  problem_event_count: number;
  frequency_pct: number | null;
  total_hours_lost: number | null;
  average_minutes_lost_per_event: number | null;
}

export interface RedzoneDowntimeProblem extends RedzoneDowntimeLocation {
  problem_category: 'Availability';
  availability_filter_status: 'Included';
  availability_filter_reason: string;
  problem_location: string | null;
  problem_type: string | null;
  problem_type_name: string | null;
  problem_type_asset_type_name: string | null;
  problem_type_asset_name: string | null;
  hierarchy_level: string;
  caution_level: 'High' | 'Medium' | 'Watch' | string;
  last_seen: string | null;
  comment_examples: string | null;
  sample_run_ids: string | null;
}

export interface RedzoneDowntimeHierarchyLevel {
  key: string;
  label: string;
  field: string;
}

export interface RedzoneDowntimePriorityPath {
  top_areas: RedzoneDowntimeProblem[];
  top_locations: RedzoneDowntimeProblem[];
  top_problem_types: RedzoneDowntimeProblem[];
  top_asset_types: RedzoneDowntimeProblem[];
  top_assets: RedzoneDowntimeProblem[];
}

export interface RedzoneDowntimeAiInsight {
  level_key: string;
  level_label: string;
  headline: string;
  evidence: string;
  recommendation: string;
}

export interface RedzoneDowntimeAiInsights {
  status: 'generated' | 'fallback' | string;
  source: string;
  model: string | null;
  message: string | null;
  most_affecting_level: {
    key: string;
    label: string;
    value: string;
    reason: string;
  };
  level_insights: RedzoneDowntimeAiInsight[];
  prescription: {
    priority: string;
    actions: string[];
    validation: string;
  };
}

export interface RedzoneDowntimeImpact {
  merged_downtime_hours: number | null;
  potential_units_lost: number | null;
  potential_units_uom: string | null;
  potential_units_by_uom?: Array<{
    uom: string;
    potential_units_lost: number;
  }>;
  overhead_rate_per_uom: number | null;
  estimated_overhead_exposure_usd: number | null;
  valuation_status: string;
  valuation_coverage_pct: number | null;
  valued_problem_events: number;
  total_problem_events: number;
  assumption_note: string;
  currency?: 'USD';
  labor_cost_included?: false;
  valuation_basis?: 'labor_credit_per_unit';
  separate_labor_cost_added?: false;
  non_labor_overhead_included?: false;
  kpi_formulas: RedzoneDowntimeKpiFormula[];
}

export interface RedzoneDowntimeKpiFormula {
  key: string;
  label: string;
  value_field: string;
  expression: string;
  explanation: string;
  source_columns: string[];
}

export interface RedzoneDowntimePayload {
  schema_version: string;
  selection: {
    sku: string;
    product_type_name: string;
    sku_label: string;
    data_through_date: string;
    plant?: string | null;
    configuration_key?: string;
    date_range?: {
      start_datetime: string | null;
      end_datetime: string | null;
    };
    archive_rule: string;
    problem_scope: string;
  };
  baseline: {
    sku: string;
    product_type_name: string;
    sku_label: string;
    historical_runs: number;
    historical_run_uuid_segments: number;
    area_count: number;
    average_run_time_minutes: number | null;
    average_oee_pct: number | null;
    average_target_oee_pct: number | null;
    average_oee_delta_pp: number | null;
    availability_problem_events: number;
    availability_hours_lost: number;
    availability_problem_groups: number;
  };
  areas: RedzoneDowntimeArea[];
  locations: RedzoneDowntimeLocation[];
  problems: RedzoneDowntimeProblem[];
  drilldowns: RedzoneDowntimeProblem[];
  hierarchy_levels: RedzoneDowntimeHierarchyLevel[];
  priority_path: RedzoneDowntimePriorityPath;
  ai_insights: RedzoneDowntimeAiInsights;
  impact?: RedzoneDowntimeImpact | null;
  narrative: {
    headline: string;
    summary: string;
    action: string;
  };
  methodology: {
    source_tables: string[];
    category_column: string;
    category_filter: string;
    join_key: string;
    run_id_rule: string;
    oee_rule: string;
    performance_included: boolean;
    quality_included: boolean;
    source_row_counts?: {
      run_fact_rows?: number;
      run_problem_rows?: number;
      comment_rows?: number;
    };
    sql: string;
  };
}

export interface RedzoneDowntimeDashboardResponse {
  analysis_id: string;
  generated_at: string;
  is_active: boolean;
  payload: RedzoneDowntimePayload;
}

export interface RedzoneDowntimeSkusResponse {
  data_through_date: string | null;
  options: RedzoneDowntimeSkuOption[];
}

export interface RedzoneDynamicRow {
  metric_level: string;
  grain: RedzoneDynamicDimension | 'overall';
  group_key: string;
  group_label: string;
  sku: string | null;
  product_name: string | null;
  problem_type_name: string | null;
  area_name: string | null;
  location_name: string | null;
  asset_type_name: string | null;
  asset_name: string | null;
  historical_runs: number;
  sku_count: number;
  area_count: number;
  location_count: number;
  average_run_time_minutes: number | null;
  average_oee_pct: number | null;
  average_target_oee_pct: number | null;
  average_oee_delta_pp: number | null;
  affected_runs: number;
  problem_event_count: number;
  frequency_pct: number | null;
  total_hours_lost: number | null;
  last_seen: string | null;
  caution_level: string;
}

export interface RedzoneDynamicPayload {
  schema_version: string;
  selection: {
    plant?: string | null;
    grain: RedzoneDynamicDimension;
    grain_label: string;
    filters: RedzoneDynamicFilters;
    configuration_key: string;
    data_through_date: string;
    problem_scope: string;
    planned_mode?: RedzonePlannedMode;
    date_range?: {
      start_datetime: string | null;
      end_datetime: string | null;
    };
    period: RedzoneDynamicPeriod;
  };
  baseline: {
    historical_runs: number;
    sku_count: number;
    area_count: number;
    location_count: number;
    average_run_time_minutes: number | null;
    average_oee_pct: number | null;
    average_target_oee_pct: number | null;
    average_oee_delta_pp: number | null;
    availability_affected_runs: number;
    availability_problem_events: number;
    availability_frequency_pct: number | null;
    availability_hours_lost: number;
  };
  groups: RedzoneDynamicRow[];
  breakdowns: Record<RedzoneDynamicDimension, RedzoneDynamicRow[]>;
  drilldowns: RedzoneDynamicRow[];
  hierarchy_levels: RedzoneDowntimeHierarchyLevel[];
  impact_lineage: Partial<Record<RedzoneDynamicDimension, RedzoneDynamicRow | null>>;
  impact: RedzoneDowntimeImpact;
  ai_insights: RedzoneDowntimeAiInsights;
  narrative: {
    headline: string;
    summary: string;
    action: string;
  };
  methodology: {
    run_filters: RedzoneDynamicDimension[];
    event_filters: RedzoneDynamicDimension[];
    oee_note: string;
    source_row_counts?: {
      run_fact_rows?: number;
      run_problem_rows?: number;
      comment_rows?: number;
    };
  };
}

export interface RedzoneDynamicFacetsResponse {
  data_through_date: string | null;
  options: RedzoneDynamicFacet[];
}

export interface RedzoneDynamicDashboardResponse {
  analysis_id: string;
  generated_at: string;
  is_active: boolean;
  payload: RedzoneDynamicPayload;
}

export interface RedzoneDynamicSavedReportRequest {
  plant: string;
  grain: RedzoneDynamicDimension;
  filters: RedzoneDynamicFilters;
  start_date: string;
  end_date: string;
  planned_mode: RedzonePlannedMode;
}

export interface RedzoneSkuReadinessSavedReportRequest {
  plant: string;
  sku: string;
  start_date: string;
  end_date: string;
}

export interface RedzoneDynamicSavedReportResponse {
  found: boolean;
  message?: string;
  report?: RedzoneDynamicDashboardResponse | RedzoneDowntimeDashboardResponse;
}

export interface RedzoneDynamicRefreshStartResponse {
  pipeline_run_id: string;
  refresh_run_id: string;
  status: 'pending';
  configuration_key: string;
}

export interface RedzoneDynamicRefreshStatusResponse {
  pipeline_run_id: string;
  refresh_run_id: string | null;
  status: 'pending' | 'running' | 'success' | 'failed' | string;
  error_message: string | null;
  analysis_id: string | null;
  generated_at: string | null;
  source_row_counts: Record<string, number>;
}

export const redzoneDowntimeApi = {
  async listSkus(): Promise<RedzoneDowntimeSkusResponse> {
    const { data } = await api.get<RedzoneDowntimeSkusResponse>('/api/v1/redzone-downtime/skus');
    return data;
  },
  async listSkuCatalog(plant: string): Promise<RedzoneDowntimeSkusResponse> {
    const { data } = await api.get<RedzoneDowntimeSkusResponse>(
      '/api/v1/redzone-downtime/skus/catalog',
      { params: { plant } },
    );
    return data;
  },
  async listDynamicFacets(plant?: string | null): Promise<RedzoneDynamicFacetsResponse> {
    const { data } = await api.get<RedzoneDynamicFacetsResponse>(
      '/api/v1/redzone-downtime/dynamic-facets/catalog',
      { params: plant ? { plant } : undefined },
    );
    return data;
  },
  async viewSavedDynamicDashboard(
    request: RedzoneDynamicSavedReportRequest,
  ): Promise<RedzoneDynamicSavedReportResponse> {
    const { data } = await api.post<RedzoneDynamicSavedReportResponse>(
      '/api/v1/redzone-downtime/dynamic-dashboard/saved',
      request,
    );
    return data;
  },
  async refreshDynamicDashboard(
    request: RedzoneDynamicSavedReportRequest,
  ): Promise<RedzoneDynamicRefreshStartResponse> {
    const { data } = await api.post<RedzoneDynamicRefreshStartResponse>(
      '/api/v1/redzone-downtime/dynamic-dashboard/refresh',
      request,
    );
    return data;
  },
  async getDynamicRefreshStatus(
    pipelineRunId: string,
  ): Promise<RedzoneDynamicRefreshStatusResponse> {
    const { data } = await api.get<RedzoneDynamicRefreshStatusResponse>(
      `/api/v1/redzone-downtime/dynamic-dashboard/refresh/${pipelineRunId}`,
    );
    return data;
  },
  async viewSavedSkuReadinessDashboard(
    request: RedzoneSkuReadinessSavedReportRequest,
  ): Promise<RedzoneDynamicSavedReportResponse> {
    const { data } = await api.post<RedzoneDynamicSavedReportResponse>(
      '/api/v1/redzone-downtime/sku-dashboard/saved',
      request,
    );
    return data;
  },
  async refreshSkuReadinessDashboard(
    request: RedzoneSkuReadinessSavedReportRequest,
  ): Promise<RedzoneDynamicRefreshStartResponse> {
    const { data } = await api.post<RedzoneDynamicRefreshStartResponse>(
      '/api/v1/redzone-downtime/sku-dashboard/refresh',
      request,
    );
    return data;
  },
  async getSkuReadinessRefreshStatus(
    pipelineRunId: string,
  ): Promise<RedzoneDynamicRefreshStatusResponse> {
    const { data } = await api.get<RedzoneDynamicRefreshStatusResponse>(
      `/api/v1/redzone-downtime/sku-dashboard/refresh/${pipelineRunId}`,
    );
    return data;
  },
  async getDynamicDashboard(
    grain: RedzoneDynamicDimension,
    filters: RedzoneDynamicFilters,
    periodStartMonth: string | null,
    periodEndMonth: string | null,
    refresh = false,
  ): Promise<RedzoneDynamicDashboardResponse> {
    const { data } = await api.post<RedzoneDynamicDashboardResponse>(
      '/api/v1/redzone-downtime/dynamic-dashboard',
      {
        grain,
        filters,
        period_start_month: periodStartMonth,
        period_end_month: periodEndMonth,
        refresh,
      },
    );
    return data;
  },
  async getDashboard(sku: string): Promise<RedzoneDowntimeDashboardResponse> {
    const { data } = await api.get<RedzoneDowntimeDashboardResponse>('/api/v1/redzone-downtime/dashboard', {
      params: { sku },
    });
    return data;
  },
  async refreshDashboard(sku: string): Promise<RedzoneDowntimeDashboardResponse> {
    const { data } = await api.post<RedzoneDowntimeDashboardResponse>(
      '/api/v1/redzone-downtime/dashboard/refresh',
      undefined,
      { params: { sku } },
    );
    return data;
  },
};
